import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiUpload, FiImage, FiX, FiAlertCircle, FiCheckCircle, FiLoader } = FiIcons;

function ProfileImageUpload({ userId, currentImageUrl, onImageUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentImageUrl || '');
  const [dragActive, setDragActive] = useState(false);

  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const validateFile = (file) => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only JPEG, PNG, and WebP images are allowed';
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return 'Image must be smaller than 2MB';
    }

    return null;
  };

  const uploadImage = async (file) => {
    try {
      setUploading(true);
      setError('');

      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Delete old image if exists
      if (currentImageUrl) {
        const oldPath = currentImageUrl.split('/').slice(-2).join('/');
        await supabase.storage
          .from('profile-images')
          .remove([oldPath]);
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/profile-${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(fileName);

      // Update profile with new image URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_image: publicUrl })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      setPreview(publicUrl);
      onImageUpdate(publicUrl);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleRemoveImage = async () => {
    if (!window.confirm('Are you sure you want to remove your profile image?')) {
      return;
    }

    try {
      setUploading(true);
      setError('');

      // Delete from storage
      if (currentImageUrl) {
        const oldPath = currentImageUrl.split('/').slice(-2).join('/');
        await supabase.storage
          .from('profile-images')
          .remove([oldPath]);
      }

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_image: null })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      setPreview('');
      onImageUpdate(null);
    } catch (err) {
      console.error('Error removing image:', err);
      setError(err.message || 'Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      <div className="flex items-center space-x-4">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden border-2 border-gray-200">
          {preview ? (
            <img
              src={preview}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-800 mb-1">Profile Picture</h4>
          <p className="text-sm text-gray-600 mb-2">
            Upload a professional photo (max 2MB)
          </p>
          <div className="flex items-center space-x-2">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                disabled={uploading}
                className="hidden"
              />
              <span className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                <SafeIcon icon={uploading ? FiLoader : FiUpload} className={`w-4 h-4 ${uploading ? 'animate-spin' : ''}`} />
                <span>{uploading ? 'Uploading...' : 'Upload Photo'}</span>
              </span>
            </label>
            {preview && (
              <button
                onClick={handleRemoveImage}
                disabled={uploading}
                className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Drag and Drop Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-blue-50'
            : 'border-gray-300 bg-gray-50'
        }`}
      >
        <SafeIcon icon={FiUpload} className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-1">
          Drag and drop your image here, or click Upload Photo
        </p>
        <p className="text-xs text-gray-500">
          JPEG, PNG, or WebP • Max 2MB
        </p>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2"
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
        <div className="flex items-start space-x-2">
          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-sm font-semibold text-gray-800 mb-1">
              Profile Picture Tips
            </h5>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>• Use a clear, well-lit headshot or action shot</li>
              <li>• Ensure your face is visible and centered</li>
              <li>• Avoid group photos or heavily filtered images</li>
              <li>• Professional appearance makes a great first impression</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileImageUpload;